import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function InsidersAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Transactions - Insiders API" description="Query transactions data via the Insiders API; request parameters, code examples and the response schema for the transactions endpoint."  canonicalUrl="https://axionquant.com/docs/03-fundamentals/insiders/transactions" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Transactions - Insiders API</DocHeading>

      <div className="space-y-12" ref={sectionRef}>

        {/* ========== 6. Insider Transactions ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /insiders/:ticker/transactions
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns a detailed list of recent insider transactions (Forms 4 and 5).
                Each entry includes the insider name, relationship, transaction date,
                shares traded, price, and type of transaction (purchase, sale, gift, etc.).
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Required</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stock ticker symbol</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/insiders/AAPL/transactions', '/insiders/AAPL/transactions?limit=50']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">shares</p>
                  <p className="text-sm text-gray-600">Number of shares involved in the transaction</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">value</p>
                  <p className="text-sm text-gray-600">Total transaction value (shares × price)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">transactionText</p>
                  <p className="text-sm text-gray-600">Human-readable description of the transaction</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">filerName</p>
                  <p className="text-sm text-gray-600">Name of the insider</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">filerRelation</p>
                  <p className="text-sm text-gray-600">Title or relationship of the filer</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">startDate</p>
                  <p className="text-sm text-gray-600">Transaction date</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ownership</p>
                  <p className="text-sm text-gray-600">Direct (D) or indirect (I) ownership</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Insider Transactions (Apple)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Insider Transactions (Apple)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="insiders/ticker/transactions"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/insiders/transactions"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
